import type { Coordinates, DrawerPosition } from '$lib/components/map/types';
import type { DateRange, MainPoiCategory } from '$lib/components/pois/types';
import type { SearchAddressResult } from '$lib/components/search/address/types';
import type { ItineraryStore } from '$lib/components/search/itinerary/ItineraryStore';
import type { WayPoint } from '$lib/components/search/itinerary/types';
import type { TrafficInformationSummary } from '$lib/components/traffic/types';
import type {
	SelectedAddress,
} from '$lib/services/urls/searchParams';
import type { LngLatBounds } from 'maplibre-gl';
import type { LayerName } from './map-styles';
import { browser } from '$app/environment';
import { createMapSettingsUserPreferencesLocalStorage } from '$lib/components/map/mapTools/mapSettingsUserPreferences';
import { createItineraryStore } from '$lib/components/search/itinerary/ItineraryStore';
import {
	createTripOptionsStore,
	type TripOptionsStore,
} from '$lib/components/search/itinerary/tripOptionsStore';
import { getContext, setContext } from 'svelte';
import { derived, get, type Readable, type Writable, writable } from 'svelte/store';

const STORE_KEY = Symbol('map');

const defaultLngLat: Coordinates = {
	lng: 0,
	lat: 0,
};
const INITIAL_LAYER_NAME: LayerName = 'michelin';

export interface ApplyPoiCategoriesChangeToSearchParamsOptions {
	selectedAddress?: SelectedAddress;
}

interface MapLayoutContext {
	isTrafficInfoPageSwitchOn: Writable<boolean>;
	isLowEmissionZoneLayerVisible: Writable<boolean>;
	isTrafficInfoVisible: Writable<boolean>;
	isSummaryOpen: Writable<boolean>;
	isOneRoutePolylinesVisible: Writable<boolean>;
	mapCenter: Writable<Coordinates>;
	selectedAddress: Writable<SearchAddressResult | null>;
	toggleDrawerScrollPosition: (bottomOffset?: number) => void;
	moveDrawer: (position: DrawerPosition | number, options?: { behavior?: ScrollBehavior }) => void;
	movePanel: (position: number, options?: { behavior?: ScrollBehavior }) => void;
	getPartialDrawerPosition: () => number;
	closeDatasheet: () => void;
	poiCoordinates: Writable<Coordinates>;
	drawerScrollPosition: Readable<number>;
	panelScrollPosition: Writable<number>;
	drawerScrollPositionName: Readable<DrawerPosition>;
	itineraryStore: ItineraryStore;
	hoveredPoiId: Writable<string | null>;
	mapController: Writable<{
		center: (center: Coordinates, zoom: number) => void;
		fitCoordinates: (coordinates?: number[][]) => void;
		fitCircle: (center: Coordinates, radius: number) => void;
		getViewPortBoundsFromCenter: (center: Coordinates, zoom: number) => LngLatBounds;
		getBounds: () => LngLatBounds;
	}>;
	mapLayerName: Writable<LayerName>;
	mapStyleUrl: Readable<string>;
	tripOptionsStore: TripOptionsStore;
	isRefreshButtonVisible: Writable<boolean>;
	trafficEventSummary: Writable<TrafficInformationSummary | null>;
	clickedPosition: Writable<Coordinates | undefined>;
	isMapLoaded: Writable<boolean>;
	isPinMarkerOnMapWithSummary: Writable<boolean>;
	isMapFullscreen: Writable<boolean>;
	mapSettingsUserPreferencesLocalStorage: ReturnType<
		typeof createMapSettingsUserPreferencesLocalStorage
	>;
	poiMarkerWithDetailsEnabled: Writable<boolean>;
	handlePoiMarkerWithDetailsByFilterChange: () => void;
	partnerBoxMapsAreReduced: Writable<boolean>;
	partnerBoxMapsContainerExpandedWidth: Writable<string | undefined>;
	mapPoiCategories: Readable<MainPoiCategory[]> & {
		emitUpdate: (updatedPoiCategories: MainPoiCategory[], options?: ApplyPoiCategoriesChangeToSearchParamsOptions) => void;
	};
	onAccommodationSelectDates: (dateRange: DateRange) => void;
	onAccommodationClearDates: () => void;
	onMichelinSelectionChange: (isOnlyMichelin: boolean) => void;
}

interface MapLayoutContextArgs {
	toggleDrawerScrollPosition: (bottomOffset?: number) => void;
	closeDatasheet: () => void;
	moveDrawer: (position: DrawerPosition | number) => void;
	movePanel: (position: number, options?: { behavior?: ScrollBehavior }) => void;
	getPartialDrawerPosition: () => number;
	drawerScrollPosition: Writable<number>;
	panelScrollPosition: Writable<number>;
	drawerScrollPositionName: Readable<DrawerPosition>;
	isTrafficPage: boolean;
	layerUrls: Record<LayerName, string>;
	itineraryFromSearchParams?: WayPoint[];
	mapPoiCategories: Writable<MainPoiCategory[]>;
	onEmitUpdateMapPoiCategoriesChange: (updatedPoiCategories: MainPoiCategory[], options?: ApplyPoiCategoriesChangeToSearchParamsOptions) => void;
	onAccommodationSelectDates: (dateRange: DateRange) => void;
	onAccommodationClearDates: () => void;
	onMichelinSelectionChange: (isOnlyMichelin: boolean) => void;
}

export function createMapLayoutContext({
	toggleDrawerScrollPosition,
	drawerScrollPosition,
	panelScrollPosition,
	drawerScrollPositionName,
	closeDatasheet,
	moveDrawer,
	movePanel,
	getPartialDrawerPosition,
	isTrafficPage,
	layerUrls,
	itineraryFromSearchParams,
	mapPoiCategories,
	onEmitUpdateMapPoiCategoriesChange,
	onAccommodationSelectDates,
	onAccommodationClearDates,
	onMichelinSelectionChange,
}: MapLayoutContextArgs) {
	const mapSettingsUserPreferencesLocalStorage
		= createMapSettingsUserPreferencesLocalStorage(browser);

	const mapLayerName = writable<LayerName>(
		mapSettingsUserPreferencesLocalStorage.get('mapStyleLayerName') ?? INITIAL_LAYER_NAME,
	);

	let initialPoiMarkerWithDetailsValue = false;

	const shouldShowPoiMarkerWithDetails = mapSettingsUserPreferencesLocalStorage.get('showPoiMarkerWithDetails') === true
	|| (!mapSettingsUserPreferencesLocalStorage.has('showPoiMarkerWithDetails') && mapSettingsUserPreferencesLocalStorage.get('hasAutomaticallyShowPoiMarkerWithDetails') === true);

	if (shouldShowPoiMarkerWithDetails) {
		initialPoiMarkerWithDetailsValue = true;
	}

	const poiMarkerWithDetailsEnabled = writable(initialPoiMarkerWithDetailsValue);

	function handlePoiMarkerWithDetailsByFilterChange() {
		if (
			!get(poiMarkerWithDetailsEnabled)
			&& !mapSettingsUserPreferencesLocalStorage.has('hasAutomaticallyShowPoiMarkerWithDetails')
		) {
			mapSettingsUserPreferencesLocalStorage.set('hasAutomaticallyShowPoiMarkerWithDetails', true);
			poiMarkerWithDetailsEnabled.set(true);
		}
	}

	const map: MapLayoutContext = {
		isTrafficInfoPageSwitchOn: writable(false),
		isLowEmissionZoneLayerVisible: writable(
			mapSettingsUserPreferencesLocalStorage.has('showLezLayer')
				? mapSettingsUserPreferencesLocalStorage.get('showLezLayer')
				: false,
		),
		isTrafficInfoVisible: writable(
			isTrafficPage
			|| (mapSettingsUserPreferencesLocalStorage.has('showTrafficLayer')
				? mapSettingsUserPreferencesLocalStorage.get('showTrafficLayer')
				: false),
		),
		isSummaryOpen: writable(false),
		isOneRoutePolylinesVisible: writable(false),
		mapCenter: writable(defaultLngLat),
		selectedAddress: writable(null),
		toggleDrawerScrollPosition,
		moveDrawer,
		movePanel,
		getPartialDrawerPosition,
		closeDatasheet,
		drawerScrollPosition,
		panelScrollPosition,
		drawerScrollPositionName,
		poiCoordinates: writable(),
		itineraryStore: createItineraryStore(itineraryFromSearchParams),
		hoveredPoiId: writable(null),
		mapController: writable({
			center: () => {},
			fitCoordinates: () => {},
			fitCircle: () => {},
			getViewPortBoundsFromCenter: () => {
				throw new Error('getViewPortBoundsFromCenter is not initialized');
			},
			getBounds: () => {
				throw new Error('getBounds is not initialized');
			},
		}),
		mapLayerName,
		mapStyleUrl: derived(mapLayerName, $mapLayerName => layerUrls[$mapLayerName]),
		tripOptionsStore: createTripOptionsStore(),
		isRefreshButtonVisible: writable(false),
		trafficEventSummary: writable(null),
		clickedPosition: writable(undefined),
		isMapLoaded: writable(false),
		isPinMarkerOnMapWithSummary: writable(false),
		isMapFullscreen: writable(false),
		mapSettingsUserPreferencesLocalStorage,
		poiMarkerWithDetailsEnabled,
		handlePoiMarkerWithDetailsByFilterChange,
		partnerBoxMapsAreReduced: writable(false),
		partnerBoxMapsContainerExpandedWidth: writable(undefined),
		mapPoiCategories: {
			subscribe: mapPoiCategories.subscribe,
			emitUpdate: onEmitUpdateMapPoiCategoriesChange,
		},
		onAccommodationSelectDates(...args) {
			poiMarkerWithDetailsEnabled.set(true);
			onAccommodationSelectDates(...args);
		},
		onAccommodationClearDates() {
			poiMarkerWithDetailsEnabled.set(false);
			onAccommodationClearDates();
		},
		onMichelinSelectionChange(isOnlyMichelin) {
			handlePoiMarkerWithDetailsByFilterChange();
			onMichelinSelectionChange(isOnlyMichelin);
		},
	};
	setContext(STORE_KEY, map);
}

export function getMapLayoutContext() {
	return getContext<MapLayoutContext>(STORE_KEY);
}
